/**
 * The app's legal footer: legal links, a rule above them, and nothing else.
 *
 * WHY THIS DID NOT EXIST BEFORE. The application has never had a footer of any kind —
 * `LicenseTrustFooter` is a panel on /licenses and not page furniture — because every
 * screen is a single scrolling column under a sidebar and nothing needed one. §4 asks for
 * the legal documents to be reachable from the footer, so this is the smallest thing that
 * satisfies it: a row of links at the end of the content column. It is NOT a place to
 * hang a copyright line, a social row or a sitemap on later.
 *
 * WHY IT IS A COMPONENT SHARED BY THE SHELL AND THE AUTH SCREEN. §4 wants these links
 * from the footer AND from signup/auth. The signup form has its own links inside the
 * consent checkbox — those are the ones being agreed to, and they need to be exactly
 * there — but the LOGIN half of that screen shows no checkbox, so without this row a
 * returning user has no route to the documents at all. Same component both places, so the
 * links, their order and their wording have one definition.
 *
 * NOT RENDERED ON THE STUDIO WORKSPACE. That route replaces the content column with a
 * full-height grid (`.lyx-content:has(> .lyx-ws)` sets `height: 100%`), so appending a
 * row after it would push the timeline out of the viewport. The workspace is a tool, and
 * it is reachable only from inside the app where every other page carries the footer. See
 * the guard at the mount in AppShell.
 */
import { Link } from 'react-router-dom';
import { LEGAL_DOCUMENTS, LEGAL_DOCUMENT_ORDER } from '../../data/legal';
import { c } from '../../styles/theme';
import { paths } from '../../routes/paths';

export interface LegalFooterProps {
  /**
   * Extra top margin, for the auth card where the row sits under a bordered box rather
   * than at the end of a scrolling page.
   */
  marginTop?: number;
  /** Centres the links, which is what the auth card wants and the content column does not. */
  centered?: boolean;
}

export default function LegalFooter({ marginTop = 40, centered = false }: LegalFooterProps) {
  return (
    <footer
      // A real <footer> with a name, so a screen reader can jump to it and so it is not
      // four anonymous links at the end of the document.
      aria-label="Legal"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 14px',
        justifyContent: centered ? 'center' : 'flex-start',
        marginTop,
        paddingTop: 14,
        borderTop: `1px solid ${c.border}`,
      }}
    >
      {LEGAL_DOCUMENT_ORDER.map((id) => (
        <Link
          key={id}
          to={paths.legal(id)}
          /*
            text2 rather than the accent, and NOT text3.

            Not the accent because this is standing furniture on every page, and four amber
            links at the foot of every screen would compete with the content above them.

            Not text3 — which is what it was first written as — because text3 measures
            3.29:1 on `bg` and 2.54:1 on `surface3`, below AA on every surface in the app,
            and these are links somebody has to be able to read to use. styles/theme.ts
            states the rule plainly (text2 for anything read, text3 for decoration) and
            styles/contrast.test.ts enforces it at the call site; the muting here is done
            with size instead, which is what that file's header prescribes.
          */
          style={{ fontSize: 11, color: c.text2, textDecoration: 'none' }}
        >
          {LEGAL_DOCUMENTS[id].title}
        </Link>
      ))}
    </footer>
  );
}
